import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { HardDrive, AlertCircle, Loader2, Download, ShieldCheck } from "lucide-react";

/**
 * AdminStorageWidget
 *
 * Renders on /account ONLY for admins. Surfaces:
 * - total storage used across all users (MB / GB)
 * - host container disk free / used / migration threshold
 * - per-tier breakdown
 * - top 10 heaviest users
 * - one-click encrypted Mongo backup download
 *
 * Quietly returns null for non-admins (403 from /admin/storage).
 */
export default function AdminStorageWidget() {
 const [data, setData] = useState(null);
 const [error, setError] = useState(null);
 const [loading, setLoading] = useState(true);
 const [backingUp, setBackingUp] = useState(false);

 useEffect(() => {
 let alive = true;
 api.get("/admin/storage")
.then(({ data }) => alive && setData(data))
.catch(e => {
 if (!alive) return;
 if (e.response?.status !== 403) setError(e.response?.data?.detail || "Failed to load storage stats");
 })
.finally(() => alive && setLoading(false));
 return () => { alive = false; };
 }, []);

 const createBackup = async () => {
 setBackingUp(true);
 try {
 const { data } = await api.post("/admin/backup/create");
 toast.success(`Backup ready (${data.size_mb} MB). download starting…`, { duration: 7000 });
 // Trigger browser download
 const resp = await api.get(data.download_path, { responseType: "blob" });
 const url = window.URL.createObjectURL(new Blob([resp.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = data.filename;
 document.body.appendChild(a);
 a.click();
 a.remove();
 window.URL.revokeObjectURL(url);
 } catch (e) {
 toast.error(e.response?.data?.detail || "Backup failed");
 } finally {
 setBackingUp(false);
 }
 };

 if (loading) return null;
 if (error) {
 return (
 <div className="card mb-6 border-l-4 border-l-[#B35D4C]" data-testid="admin-storage-error">
 <div className="flex items-center gap-2 text-[#B35D4C] text-sm">
 <AlertCircle size={14} strokeWidth={1.5} /> {error}
 </div>
 </div>
 );
 }
 if (!data) return null; // non-admin

 const disk = data.host_disk || {};
 const usedPct = disk.used_pct || 0;
 const warnColor =
 usedPct >= 90 ? "bg-[#B35D4C]" :
 usedPct >= 70 ? "bg-[#C9A646]" : "bg-[#1B6C62]";

 return (
 <div className="card mb-6 border-l-4 border-l-[#1B6C62]" data-testid="admin-storage-widget">
 <div className="flex items-center gap-2 mb-1">
 <HardDrive size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">Storage</h3>
 <span className="label-mono text-[10px] text-[#1B6C62]">Admin only</span>
 </div>
 <p className="text-[12px] text-[#5a6b7a] mb-3">
 Migrate to Cloudflare R2 when host disk hits 70%.
 </p>

 {/* Host disk meter */}
 <div className="mb-4">
 <div className="flex justify-between text-[11px] text-[#5a6b7a] mb-1">
 <span>Host container disk</span>
 <span className="font-mono">
 {disk.used_gb} GB / {disk.total_gb} GB ({usedPct}%)
 </span>
 </div>
 <div className="w-full h-2 bg-[#EFEDE5] rounded">
 <div
 className={`h-2 rounded ${warnColor}`}
 style={{ width: `${Math.min(100, usedPct)}%` }}
 data-testid="admin-storage-disk-bar"
 />
 </div>
 {disk.should_migrate && (
 <p className="text-[12px] text-[#B35D4C] mt-1.5 flex items-center gap-1">
 <AlertCircle size={12} strokeWidth={1.5} />
 Host disk &gt;70%. time to migrate to R2.
 </p>
 )}
 </div>

 {/* User storage summary */}
 <div className="grid grid-cols-3 gap-3 mb-4">
 <div className="border border-[#EFEDE5] p-2">
 <div className="label-mono text-[10px] text-[#5a6b7a]">Total used</div>
 <div className="font-serif text-lg text-[#0D2B52]" data-testid="admin-storage-total">
 {data.total_gb} GB
 </div>
 </div>
 <div className="border border-[#EFEDE5] p-2">
 <div className="label-mono text-[10px] text-[#5a6b7a]">Users</div>
 <div className="font-serif text-lg text-[#0D2B52]">{data.total_users}</div>
 </div>
 <div className="border border-[#EFEDE5] p-2">
 <div className="label-mono text-[10px] text-[#5a6b7a]">Docs</div>
 <div className="font-serif text-lg text-[#0D2B52]">{data.doc_count}</div>
 </div>
 </div>

 {/* By tier */}
 {data.by_tier?.length > 0 && (
 <div className="mb-4">
 <div className="label-mono text-[10px] text-[#5a6b7a] mb-1">By tier</div>
 <div className="text-[12px] text-[#0D2B52] font-mono space-y-0.5">
 {data.by_tier.map(t => (
 <div key={t.tier} className="flex justify-between">
 <span>{t.tier} ({t.users}u)</span>
 <span>{t.mb} MB / {t.users * t.limit_mb} MB cap</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Top users */}
 {data.top_users?.length > 0 && (
 <div className="mb-4">
 <div className="label-mono text-[10px] text-[#5a6b7a] mb-1">Top users</div>
 <div className="text-[12px] text-[#0D2B52] font-mono space-y-0.5">
 {data.top_users.slice(0, 5).map(u => (
 <div key={u.email} className="flex justify-between">
 <span className="truncate max-w-[60%]">{u.email}</span>
 <span>{u.mb} / {u.limit_mb} MB</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Backup button */}
 <div className="border-t border-[#EFEDE5] pt-3 mt-2">
 <div className="flex items-center gap-2 mb-1.5">
 <ShieldCheck size={13} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="font-serif text-[13px] text-[#0D2B52]">Encrypted Mongo backup</span>
 </div>
 <p className="text-[11px] text-[#5a6b7a] mb-2">
 Run monthly. Save to Google Drive. AES-encrypted with <code className="font-mono text-[10px]">BACKUP_PASSWORD</code> env var.
 </p>
 <button
 onClick={createBackup}
 disabled={backingUp}
 className="btn-secondary inline-flex items-center gap-1.5 text-xs px-3 py-1.5 disabled:opacity-60"
 data-testid="admin-backup-download-btn"
 >
 {backingUp ? (
 <><Loader2 size={12} strokeWidth={1.5} className="animate-spin" /> Creating dump…</>
 ) : (
 <><Download size={12} strokeWidth={1.5} /> Download backup now</>
 )}
 </button>
 </div>
 </div>
 );
}
